import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ChatCircleDots, PaperPlaneRight, Plus, Robot, Users as UsersIcon, User, Trash, Paperclip, X, DownloadSimple, FileText,
} from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function wsUrl(token) {
  const httpUrl = BACKEND;
  const proto = httpUrl.startsWith("https") ? "wss" : "ws";
  const host = httpUrl.replace(/^https?:\/\//, "");
  return `${proto}://${host}/api/ws/chat?token=${encodeURIComponent(token)}`;
}

function kindIcon(kind) {
  if (kind === "ai") return <Robot size={14} weight="fill" />;
  if (kind === "group") return <UsersIcon size={14} />;
  if (kind === "record") return <ChatCircleDots size={14} />;
  return <User size={14} />;
}

export default function ChatPage() {
  const { user } = useAuth();
  const { channelId: routeChannel } = useParams();
  const nav = useNavigate();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [users, setUsers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newKind, setNewKind] = useState("dm");
  const [newMembers, setNewMembers] = useState([]);
  const [newName, setNewName] = useState("");
  const [wsReady, setWsReady] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // [{file_name,file_url,size,content_type}]
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  async function loadChannels() {
    const { data } = await api.get("/chat/channels");
    setChannels(data || []);
  }
  async function loadUsers() {
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data || []);
    } catch {
      // finance/procurement/department don't have /auth/users; fall back to empty
      setUsers([]);
    }
  }
  useEffect(() => { loadChannels(); loadUsers(); }, []);

  // Auto-open channel from URL, else first channel — refetch list when route changes
  // so a channel created via <DiscussButton> and navigated to shows up in the list rail.
  useEffect(() => {
    if (routeChannel) {
      setActiveId(routeChannel);
      loadChannels();
      return;
    }
    if (!activeId && channels.length) setActiveId(channels[0].id);
  }, [routeChannel, channels.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeChannel = useMemo(() => {
    const found = channels.find((c) => c.id === activeId);
    if (found) return found;
    if (activeId && routeChannel === activeId) {
      return { id: activeId, name: "Opening conversation…", kind: "record", member_ids: [] };
    }
    return null;
  }, [channels, activeId, routeChannel]);

  // Load messages when active channel changes
  useEffect(() => {
    if (!activeId) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/chat/channels/${activeId}/messages`);
        if (!ignore) setMessages(data || []);
        // Mark this channel as read (both persists on server and refreshes global badge)
        try { await api.post(`/chat/channels/${activeId}/read`); } catch {}
        window.dispatchEvent(new Event("chat:unread-refresh"));
      } catch {
        if (!ignore) setMessages([]);
      }
    })();
    return () => { ignore = true; };
  }, [activeId]);

  // WebSocket
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const ws = new WebSocket(wsUrl(token));
    wsRef.current = ws;
    ws.onopen = () => setWsReady(true);
    ws.onclose = () => setWsReady(false);
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "message") {
        const m = msg.message;
        setChannels((cs) => cs.map((c) => c.id === m.channel_id ? {
          ...c, last_message_at: m.created_at, last_message_preview: (m.text || "").slice(0, 120),
        } : c));
        setMessages((prev) => (m.channel_id === activeId ? [...prev, m] : prev));
        // If message is in the currently-open channel, immediately mark read; otherwise trigger badge refresh.
        if (m.channel_id === activeId && m.sender_id !== user?.id) {
          api.post(`/chat/channels/${activeId}/read`).catch(() => {});
        }
        window.dispatchEvent(new Event("chat:unread-refresh"));
      } else if (msg.type === "delete" && msg.channel_id === activeId) {
        setMessages((prev) => prev.map((x) => x.id === msg.message_id ? { ...x, deleted_at: new Date().toISOString(), text: "" } : x));
      }
    };
    return () => { try { ws.close(); } catch {} };
  }, [activeId]);

  // Auto-scroll
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, activeId]);

  async function send(e) {
    e?.preventDefault?.();
    const t = text.trim();
    const atts = pendingFiles;
    if ((!t && atts.length === 0) || !activeId) return;
    setText("");
    setPendingFiles([]);
    try {
      const { data } = await api.post(`/chat/channels/${activeId}/messages`, { text: t, attachments: atts });
      // optimistic append (server broadcast will also arrive)
      setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
      setText(t);
      setPendingFiles(atts);
    }
  }

  async function onPickAttachments(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingFiles(true);
    const token = localStorage.getItem("access_token");
    for (const file of files) {
      try {
        const body = new FormData();
        body.append("file", file);
        const res = await fetch(`${BACKEND}/api/uploads/chat`, {
          method: "POST", headers: { Authorization: `Bearer ${token}` }, body,
        });
        if (!res.ok) throw new Error("upload failed");
        const data = await res.json();
        setPendingFiles((p) => [...p, { file_name: data.file_name, file_url: data.file_url, size: data.size, content_type: data.content_type }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploadingFiles(false);
  }

  function removePending(i) {
    setPendingFiles((p) => p.filter((_, idx) => idx !== i));
  }

  function fileHref(u) {
    if (!u) return "#";
    return u.startsWith("http") ? u : `${BACKEND}${u}`;
  }

  function isImage(ct, name) {
    if (ct && ct.startsWith("image/")) return true;
    if (name && /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(name)) return true;
    return false;
  }

  function humanSize(bytes) {
    if (!bytes) return "";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  }

  async function createChannel(e) {
    e?.preventDefault?.();
    try {
      const payload = { kind: newKind, member_ids: newMembers, name: newName || undefined };
      if (newKind === "ai") { payload.member_ids = []; payload.name = newName || "AI Assistant"; }
      const { data } = await api.post("/chat/channels", payload);
      await loadChannels();
      setActiveId(data.id);
      setNewOpen(false);
      setNewName("");
      setNewMembers([]);
      nav(`/chat/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  }

  async function deleteMsg(mid) {
    try {
      await api.delete(`/chat/messages/${mid}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  }

  return (
    <div className="space-y-4" data-testid="chat-page">
      <div className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><ChatCircleDots size={12} /> Communication</div>
          <h1 className="section-title text-3xl sm:text-4xl">Chat</h1>
          <p className="text-sm text-muted-foreground mt-1">Direct messages, group threads, and an AI assistant. <span className={wsReady ? "text-emerald-700" : "text-amber-700"}>{wsReady ? "● Live" : "○ Reconnecting"}</span></p>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="new-chat-btn"><Plus size={16} className="mr-2" /> New chat</Button>
      </div>

      <div className="grid grid-cols-[280px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Channel list */}
        <div className="border border-border rounded-md bg-card overflow-y-auto" data-testid="chat-channel-list">
          {channels.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">No conversations yet. Start a new chat to say hi.</div>
          ) : channels.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActiveId(c.id); nav(`/chat/${c.id}`); }}
              className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted flex items-start gap-2 ${activeId === c.id ? "bg-muted" : ""}`}
              data-testid={`chat-channel-${c.id}`}
            >
              <div className="mt-0.5 text-muted-foreground">{kindIcon(c.kind)}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.name || "Untitled"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{c.last_message_preview || `${c.kind} · ${c.member_ids?.length || 0} members`}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Messages */}
        <div className="border border-border rounded-md bg-card flex flex-col min-h-0">
          {activeChannel ? (
            <>
              <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                <div className="text-muted-foreground">{kindIcon(activeChannel.kind)}</div>
                <div>
                  <div className="font-heading font-bold text-sm">{activeChannel.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{activeChannel.kind}{activeChannel.record_id ? ` · ${activeChannel.record_type}` : ""}</div>
                </div>
              </div>
              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-message-list">
                {messages.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-8">Say hello — this thread is empty.</div>
                ) : messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-md px-3 py-2 border ${
                        m.is_ai ? "bg-amber-50 border-amber-200 text-amber-950"
                        : mine ? "bg-foreground text-background border-foreground"
                        : "bg-muted border-border"
                      }`}>
                        {!mine && (
                          <div className={`text-[10px] uppercase tracking-widest mb-0.5 ${m.is_ai ? "text-amber-800" : "text-muted-foreground"}`}>
                            {m.is_ai ? "AI Assistant" : `${m.sender_name}${m.sender_role ? ` · ${m.sender_role}` : ""}`}
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {m.deleted_at ? <span className="italic opacity-60">Message deleted</span> : m.text}
                        </div>
                        {!m.deleted_at && (m.attachments || []).length > 0 && (
                          <div className="mt-2 space-y-2" data-testid={`chat-msg-attachments-${m.id}`}>
                            {(m.attachments || []).map((a, i) => (
                              isImage(a.content_type, a.file_name) ? (
                                <a key={i} href={fileHref(a.file_url)} target="_blank" rel="noreferrer" className="block">
                                  <img src={fileHref(a.file_url)} alt={a.file_name} className="max-h-48 rounded border border-black/10 bg-white" />
                                </a>
                              ) : (
                                <a key={i} href={fileHref(a.file_url)} target="_blank" rel="noreferrer"
                                   className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs hover:underline ${mine ? "bg-background/10 border-background/20 text-background" : "bg-background border-border"}`}
                                   data-testid={`chat-attachment-${m.id}-${i}`}>
                                  <FileText size={14} />
                                  <span className="truncate max-w-[220px]">{a.file_name}</span>
                                  {a.size ? <span className="opacity-70 text-[10px]">({humanSize(a.size)})</span> : null}
                                  <DownloadSimple size={12} className="ml-auto" />
                                </a>
                              )
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`text-[10px] ${mine && !m.is_ai ? "text-background/70" : "text-muted-foreground"}`}>{(m.created_at || "").slice(11, 16)}</div>
                          {mine && !m.deleted_at && (
                            <button onClick={() => deleteMsg(m.id)} className={`text-[10px] ${mine ? "text-background/70 hover:text-background" : "text-muted-foreground"}`} data-testid={`chat-delete-${m.id}`}>
                              <Trash size={10} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="border-t border-border p-3 space-y-2">
                {pendingFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="chat-pending-attachments">
                    {pendingFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 border border-border rounded px-2 py-1 text-xs bg-muted/40">
                        <Paperclip size={12} />
                        <span className="truncate max-w-[180px]">{f.file_name}</span>
                        {f.size ? <span className="text-[10px] text-muted-foreground">({humanSize(f.size)})</span> : null}
                        <button type="button" onClick={() => removePending(i)} className="ml-1 text-muted-foreground hover:text-foreground" data-testid={`chat-remove-pending-${i}`}>
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input ref={fileInputRef} type="file" multiple onChange={onPickAttachments} className="hidden" data-testid="chat-file-input" />
                  <Button type="button" variant="outline" size="icon" className="border-border" onClick={() => fileInputRef.current?.click()} disabled={uploadingFiles} data-testid="chat-attach-btn" title="Attach files">
                    <Paperclip size={16} />
                  </Button>
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={uploadingFiles ? "Uploading attachment…" : (activeChannel.kind === "ai" ? "Ask the AI assistant…" : "Type a message or drop a file")}
                    className="border-border"
                    data-testid="chat-input"
                  />
                  <Button type="submit" disabled={(!text.trim() && pendingFiles.length === 0) || uploadingFiles} data-testid="chat-send-btn"><PaperPlaneRight size={16} /></Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a conversation or start a new one.</div>
          )}
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2"><Plus size={18} /> Start a new chat</DialogTitle>
            <DialogDescription>Direct message a teammate, create a group thread, or chat with the AI assistant.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createChannel} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label>
              <Select value={newKind} onValueChange={(v) => { setNewKind(v); setNewMembers([]); }}>
                <SelectTrigger data-testid="new-chat-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dm">Direct Message (1-to-1)</SelectItem>
                  <SelectItem value="group">Group chat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newKind !== "ai" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  {newKind === "dm" ? "Talk to" : "Add members"}
                </Label>
                {users.length === 0 ? (
                  <div className="text-xs text-muted-foreground border border-dashed border-border rounded p-3">
                    Team directory not visible to your role. Ask an admin to enable, or use AI Assistant.
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-border rounded">
                    {users.filter((u) => u.id !== user?.id).map((u) => (
                      <label key={u.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted">
                        <input
                          type={newKind === "dm" ? "radio" : "checkbox"}
                          name="member"
                          checked={newMembers.includes(u.id)}
                          onChange={(e) => {
                            if (newKind === "dm") setNewMembers([u.id]);
                            else setNewMembers((m) => e.target.checked ? [...m, u.id] : m.filter((x) => x !== u.id));
                          }}
                          data-testid={`new-chat-member-${u.id}`}
                        />
                        <div className="text-sm">{u.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground ml-auto">{u.role}</div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(newKind === "group" || newKind === "ai") && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name (optional)</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="border-border" data-testid="new-chat-name" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={newKind === "dm" && newMembers.length !== 1} data-testid="new-chat-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
